import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import { ResellerSummaryResponse } from '@utils/types/back-office/usage-revenue';

/**
 * Hook to fetch reseller rollup summary for usage/revenue
 *
 * GET /api/admin/usage/reseller-summary
 * Query params:
 *   - month (required, YYYY-MM)
 *   - sort: "total_cost" | "reseller_name" | "reseller_id" | "plan_name" | "stt_units" | "sub_org_count"
 *   - direction: "asc" | "desc"
 *   - limit, offset
 *   - reseller_id (filter by exact reseller id)
 *   - q (filter by reseller name)
 *   - plan_name (filter by plan)
 *   - min_stt_units (filter by minimum usage)
 */
export const useGetResellerSummary = (
  month: string | undefined,
  options?: {
    limit?: number;
    offset?: number;
    sort?: string;
    direction?: 'asc' | 'desc';
    resellerId?: number;
    search?: string;
    planName?: string;
    minUsage?: number;
  }
) => {
  const {
    limit = 25,
    offset = 0,
    sort,
    direction,
    resellerId,
    search,
    planName,
    minUsage
  } = options || {};

  const fetchResellerSummary = async () => {
    if (!month) {
      throw new Error('Month is required');
    }

    const params = new URLSearchParams({
      month,
      limit: String(limit),
      offset: String(offset)
    });

    if (sort) params.append('sort', sort);
    if (direction) params.append('direction', direction);
    if (resellerId != null) params.append('reseller_id', String(resellerId));
    if (search) params.append('q', search);
    if (planName) params.append('plan_name', planName);
    if (minUsage != null) params.append('min_stt_units', String(minUsage));

    const response = await Axios.get<ResellerSummaryResponse>(
      `${URL_EXTENSION}/admin/usage/reseller-summary?${params.toString()}`
    );
    return response.data;
  };

  return useQuery<ResellerSummaryResponse, AxiosError<BaseError>>(
    [
      'resellerSummary',
      month,
      limit,
      offset,
      sort,
      direction,
      resellerId,
      search,
      planName,
      minUsage
    ],
    fetchResellerSummary,
    {
      enabled: !!month,
      useErrorBoundary: false,
      keepPreviousData: true
    }
  );
};
